import { Alert, Button, Form, Modal, Popconfirm, Select, Typography } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDepartmentsList } from "@/modules/departments/hooks/useDepartments";
import { DepartmentTreeSelect } from "@/shared/components/DepartmentTreeSelect";
import { useRoleOptions } from "../hooks/useUsers";
import type { ManagedUser } from "../types";

const { Text } = Typography;

interface AssignRoleFormValues {
	roleId: string;
	departmentId?: string;
}

interface AssignRoleModalProps {
	open: boolean;
	user: ManagedUser | null;
	isSubmitting: boolean;
	onCancel: () => void;
	onAssign: (input: { roleId: string; scope?: string }) => void;
	onRevoke: (roleId: string) => void;
}

/** `department:‹uuid›` dan `uuid` qismini ajratadi — `UserFormModal`dagi
 * teskari yig'ish bilan bir juftlik. */
function departmentIdFromScope(scope: string | undefined): string | undefined {
	return scope?.startsWith("department:") ? scope.slice("department:".length) : undefined;
}

/**
 * §3.5 (spec 2026-09-12-p5) — ko'p-tag ro'yxati o'rniga bitta `Select`
 * (joriy rol oldindan tanlangan) + "Saqlash". `POST /role-assignments`
 * endi UPSERT (`subjectId` UNIQUE), ya'ni "Saqlash" eski rolni jimgina
 * almashtiradi. "Rolni olib tashlash" — alohida, kamdan-kam ishlatiladigan
 * amal (`onRevoke`, mavjud `DELETE` endpointga).
 */
export function AssignRoleModal({
	open,
	user,
	isSubmitting,
	onCancel,
	onAssign,
	onRevoke,
}: AssignRoleModalProps) {
	const { t } = useTranslation("users");
	const [form] = Form.useForm<AssignRoleFormValues>();

	const { data: roles = [] } = useRoleOptions(open);
	const { data: departments = [] } = useDepartmentsList();

	// Bitta-rol invarianti tufayli `user.roles` ko'pi bilan bitta element
	// saqlaydi (spec §3.2) — massiv shakli faqat API kontraktidan qolgan.
	const current = user?.roles[0];

	// `FormModal` naqshidagi kabi: modal har ochilganda joriy tayinlashga
	// qayta to'ldiriladi, aks holda oldingi foydalanuvchining tanlovi qolib
	// ketardi.
	useEffect(() => {
		if (open) {
			// `destroyOnHidden` `Form` DOM tugunini unmount qiladi, lekin
			// `useForm` do'koni qiymatlarni saqlab qoladi — `resetFields()`
			// AVVAL kerak, aks holda A foydalanuvchi (bo'lim bilan
			// cheklangan) uchun ochilgandan keyin B (cheklovsiz) uchun
			// ochilganda eski `departmentId` qolib ketardi.
			form.resetFields();
			form.setFieldsValue({
				roleId: current?.roleId,
				departmentId: departmentIdFromScope(current?.scope),
			});
		}
	}, [open, current, form]);

	return (
		<Modal
			open={open}
			title={t("assignModal.title", { phone: user?.phone ?? "" })}
			okText={t("assignModal.save")}
			cancelText={t("assignModal.close")}
			confirmLoading={isSubmitting}
			onCancel={onCancel}
			onOk={() => form.submit()}
			destroyOnHidden
			footer={(originalFooter) => (
				<div className="flex items-center justify-between">
					<div>
						{current ? (
							<Popconfirm
								title={t("assignModal.revokeConfirmTitle")}
								description={t("assignModal.revokeConfirmDescription")}
								okText={t("assignModal.revokeConfirmOk")}
								cancelText={t("assignModal.revokeConfirmCancel")}
								okButtonProps={{ danger: true }}
								onConfirm={() => onRevoke(current.roleId)}
							>
								<Button danger>{t("assignModal.revoke")}</Button>
							</Popconfirm>
						) : null}
					</div>
					<div className="flex gap-2">{originalFooter}</div>
				</div>
			)}
		>
			{current ? (
				<Text type="secondary" className="block mb-4">
					{t("assignModal.currentRole")}: <strong>{current.role?.name ?? current.roleId}</strong>
					{current.scope ? ` · ${current.scope}` : ""}
				</Text>
			) : (
				<Alert className="mb-4" type="warning" showIcon message={t("assignModal.noRole")} />
			)}

			<Form
				form={form}
				layout="vertical"
				onFinish={(values) => {
					onAssign({
						roleId: values.roleId,
						scope: values.departmentId ? `department:${values.departmentId}` : undefined,
					});
				}}
			>
				<Form.Item
					name="roleId"
					label={t("common.roleLabel")}
					rules={[{ required: true, message: t("common.roleRequired") }]}
				>
					<Select
						placeholder={t("common.roleRequired")}
						options={roles.map((role) => ({ value: role.id, label: role.name }))}
					/>
				</Form.Item>

				<Form.Item name="departmentId" label={t("common.departmentScopeLabel")}>
					<DepartmentTreeSelect
						departments={departments}
						placeholder={t("common.departmentScopePlaceholder")}
					/>
				</Form.Item>
			</Form>
		</Modal>
	);
}
